import type { ReactNode } from "react"

import { create } from "zustand"

import { logger } from "@/lib/logger"

const CONTENT_SIDEBAR_RATIO_STORAGE_KEY = "content-sidebar-ratio"
const DEFAULT_PANEL_RATIO = 0.35
const MIN_PANEL_RATIO = 0.2
const MAX_PANEL_RATIO = 0.8

export type ContentSidebarConfirmMessage = {
  title: string
  description: string
  confirmLabel?: string
  cancelLabel?: string
}

export type ContentSidebarHeaderMode = "default" | "custom" | "none"

export type ContentSidebarHeaderContext = {
  title?: ReactNode
  actions?: ReactNode
  isFocused: boolean
  isFocusable: boolean
  closeable: boolean
  onToggleFocus: () => void
  onClose: () => void
}

export type ContentSidebarPanel = {
  id: string
  title?: ReactNode
  headerActions?: ReactNode
  headerMode?: ContentSidebarHeaderMode
  renderHeader?: (context: ContentSidebarHeaderContext) => ReactNode
  content: ReactNode
  onClose?: () => void
  focusable?: boolean
  resizable?: boolean
  desktopWidthPx?: number
  closeable?: boolean
  isDirty?: () => boolean
  confirmMessage?: ContentSidebarConfirmMessage
  onBeforeOpen?: () => void
}

type PendingSidebarAction = { type: "open"; panel: ContentSidebarPanel } | { type: "close" }
type PendingCloseResolver = { resolve: (shouldClose: boolean) => void }

type ContentSidebarState = {
  currentPanel: ContentSidebarPanel | null
  pushedPanel: ContentSidebarPanel | null
  isOpen: boolean
  isFocused: boolean
  panelRatio: number
  pendingAction: PendingSidebarAction | null
  showConfirmDialog: boolean
  pendingCloseResolver: PendingCloseResolver | null

  openPanel: (panel: ContentSidebarPanel) => void
  closePanel: () => void
  requestClose: () => Promise<boolean>
  forceClosePanel: () => void
  pushPanel: (panel: ContentSidebarPanel) => void
  popPanel: () => void
  toggleFocus: () => void
  setPanelRatio: (ratio: number) => void
  confirmPendingAction: () => void
  cancelPendingAction: () => void
}

function clampPanelRatio(ratio: number): number {
  return Math.min(MAX_PANEL_RATIO, Math.max(MIN_PANEL_RATIO, ratio))
}

function readInitialPanelRatio(): number {
  if (typeof window === "undefined") {
    return DEFAULT_PANEL_RATIO
  }

  const storedRatio = window.localStorage.getItem(CONTENT_SIDEBAR_RATIO_STORAGE_KEY)
  if (!storedRatio) {
    return DEFAULT_PANEL_RATIO
  }

  const parsed = Number.parseFloat(storedRatio)
  if (Number.isNaN(parsed)) {
    return DEFAULT_PANEL_RATIO
  }

  return clampPanelRatio(parsed)
}

function persistPanelRatio(ratio: number): void {
  if (typeof window === "undefined") {
    return
  }

  try {
    window.localStorage.setItem(CONTENT_SIDEBAR_RATIO_STORAGE_KEY, `${ratio}`)
  } catch (error) {
    logger.warn("Could not persist content sidebar ratio", { error: error as Error })
  }
}

function isPanelDirty(panel: ContentSidebarPanel | null): boolean {
  if (!panel?.isDirty) {
    return false
  }

  try {
    return panel.isDirty()
  } catch (error) {
    logger.error("Error checking content sidebar dirty state", error as Error)
    return true
  }
}

function hasDirtyPanel(...panels: Array<ContentSidebarPanel | null>): boolean {
  return panels.some((panel) => isPanelDirty(panel))
}

function runPanelOnClose(panel: ContentSidebarPanel | null): void {
  if (!panel?.onClose) {
    return
  }

  try {
    panel.onClose()
  } catch (error) {
    logger.error("Error running content sidebar onClose", error as Error)
  }
}

function runPanelOnBeforeOpen(panel: ContentSidebarPanel): void {
  if (!panel.onBeforeOpen) {
    return
  }

  try {
    panel.onBeforeOpen()
  } catch (error) {
    logger.error("Error running content sidebar onBeforeOpen", error as Error)
  }
}

export const useContentSidebarStore = create<ContentSidebarState>((set, get) => ({
  currentPanel: null,
  pushedPanel: null,
  isOpen: false,
  isFocused: false,
  panelRatio: readInitialPanelRatio(),
  pendingAction: null,
  showConfirmDialog: false,
  pendingCloseResolver: null,

  openPanel: (panel) => {
    const state = get()
    if (state.currentPanel && isPanelDirty(state.currentPanel)) {
      set({
        pendingAction: { type: "open", panel },
        showConfirmDialog: true,
        pendingCloseResolver: null,
      })
      return
    }

    // Skip onClose when the same panel id is being re-opened — that's a
    // panel rebuild (e.g. Deep Explorer's session-change effect rebuilds
    // the panel content with the new sessionTitle), not a user-driven
    // close. Firing the old onClose in that case ran setDeepPanelOpen(false)
    // and toggled panelOpen → false, which the next-tick "close-on-flag"
    // effect interpreted as "user closed the panel" and force-closed it
    // for real (sidebar-closes-on-submit, 2026-05-04 retest).
    const isSameIdRebuild = state.currentPanel?.id === panel.id && !state.pushedPanel
    if (!isSameIdRebuild) {
      runPanelOnClose(state.currentPanel)
      runPanelOnClose(state.pushedPanel)
    }
    runPanelOnBeforeOpen(panel)
    set({
      currentPanel: panel,
      pushedPanel: null,
      isOpen: true,
      isFocused: false,
      pendingAction: null,
      showConfirmDialog: false,
      pendingCloseResolver: null,
    })
  },

  closePanel: () => {
    const state = get()
    if (!state.currentPanel) {
      set({
        isOpen: false,
        isFocused: false,
        pendingAction: null,
        showConfirmDialog: false,
        pendingCloseResolver: null,
      })
      return
    }

    if (hasDirtyPanel(state.currentPanel, state.pushedPanel)) {
      set({
        pendingAction: { type: "close" },
        showConfirmDialog: true,
        pendingCloseResolver: null,
      })
      return
    }

    get().forceClosePanel()
  },

  requestClose: () => {
    const state = get()
    if (!state.currentPanel) {
      return Promise.resolve(true)
    }
    if (!hasDirtyPanel(state.currentPanel, state.pushedPanel)) {
      get().forceClosePanel()
      return Promise.resolve(true)
    }
    return new Promise<boolean>((resolve) => {
      set({
        pendingAction: { type: "close" },
        showConfirmDialog: true,
        pendingCloseResolver: { resolve },
      })
    })
  },

  forceClosePanel: () => {
    const state = get()
    runPanelOnClose(state.currentPanel)
    runPanelOnClose(state.pushedPanel)
    set({
      currentPanel: null,
      pushedPanel: null,
      isOpen: false,
      isFocused: false,
      pendingAction: null,
      showConfirmDialog: false,
      pendingCloseResolver: null,
    })
  },

  pushPanel: (panel) => {
    const state = get()
    runPanelOnBeforeOpen(panel)
    if (state.pushedPanel) {
      // A base panel is already saved; just swap the visible panel without losing the base.
      runPanelOnClose(state.currentPanel)
      set({
        currentPanel: panel,
        isOpen: true,
        isFocused: state.isFocused,
      })
    } else {
      set({
        pushedPanel: state.currentPanel,
        currentPanel: panel,
        isOpen: true,
        isFocused: state.isFocused,
      })
    }
  },

  popPanel: () => {
    const state = get()
    if (!state.pushedPanel) {
      get().forceClosePanel()
      return
    }
    runPanelOnClose(state.currentPanel)
    set({
      currentPanel: state.pushedPanel,
      pushedPanel: null,
      isOpen: true,
      isFocused: state.isFocused,
    })
  },

  toggleFocus: () =>
    set((state) => {
      if (!state.currentPanel?.focusable) {
        return state
      }
      return { isFocused: !state.isFocused }
    }),

  setPanelRatio: (ratio) => {
    const nextRatio = clampPanelRatio(ratio)
    persistPanelRatio(nextRatio)
    set({ panelRatio: nextRatio })
  },

  confirmPendingAction: () => {
    const state = get()
    if (!state.pendingAction) {
      return
    }

    if (state.pendingAction.type === "close") {
      state.pendingCloseResolver?.resolve(true)
      get().forceClosePanel()
      return
    }

    runPanelOnClose(state.currentPanel)
    runPanelOnClose(state.pushedPanel)
    const nextPanel = state.pendingAction.panel
    runPanelOnBeforeOpen(nextPanel)
    set({
      currentPanel: nextPanel,
      pushedPanel: null,
      isOpen: true,
      isFocused: false,
      pendingAction: null,
      showConfirmDialog: false,
      pendingCloseResolver: null,
    })
  },

  cancelPendingAction: () => {
    const state = get()
    state.pendingCloseResolver?.resolve(false)
    set({
      pendingAction: null,
      showConfirmDialog: false,
      pendingCloseResolver: null,
    })
  },
}))
